import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Plus, X, Upload, Trash2 } from "lucide-react";
import { api, fileUrl, formatApiError } from "../../lib/api";
import { AdminLayout } from "./AdminLayout";

const EMPTY = { slug: "", name: "", role: "Artist", instagram: "", email: "", experience: "", photo: "", styles: [], projects: [], bio: "", tagline: "", active: true };

export default function AdminArtists() {
  const qc = useQueryClient();
  const [editing, setEditing] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [addingImg, setAddingImg] = useState(false);
  const [customStyle, setCustomStyle] = useState("");

  const { data: artists = [], isLoading } = useQuery({ queryKey: ["admin-artists"], queryFn: async () => (await api.get("/admin/artists")).data });
  const { data: styles = [] } = useQuery({ queryKey: ["styles"], queryFn: async () => (await api.get("/styles")).data });
  const { data: portfolio = [] } = useQuery({ queryKey: ["admin-portfolio"], queryFn: async () => (await api.get("/admin/portfolio")).data });

  const artistImages = editing ? portfolio.filter((p) => p.artist_slug === editing.slug) : [];

  const refetchAll = () => {
    qc.invalidateQueries({ queryKey: ["admin-artists"] });
    qc.invalidateQueries({ queryKey: ["artists"] });
    qc.invalidateQueries({ queryKey: ["admin-portfolio"] });
    qc.invalidateQueries({ queryKey: ["portfolio"] });
  };

  const save = async () => {
    if (!editing.slug || !editing.name) { toast.error("Name and slug are required"); return; }
    try {
      await api.post("/admin/artists", editing);
      toast.success("Published — changes are live");
      refetchAll();
      setEditing(null);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const toggleActive = async (a) => {
    await api.put(`/admin/artists/${a.slug}`, { ...a, active: !a.active });
    refetchAll();
  };

  const remove = async (slug) => {
    if (!window.confirm("Delete this artist?")) return;
    await api.delete(`/admin/artists/${slug}`);
    toast.success("Deleted");
    refetchAll();
  };

  const uploadPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setEditing((ed) => ({ ...ed, photo: data.path }));
      toast.success("Photo uploaded");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setUploading(false); }
  };

  const addWorkImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!editing.slug) { toast.error("Add the name & slug and Publish first, then add work images."); return; }
    setAddingImg(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await api.post("/admin/portfolio", { image: data.path, artist_slug: editing.slug, style_slug: editing.styles?.[0] || null });
      toast.success("Image added");
      qc.invalidateQueries({ queryKey: ["admin-portfolio"] });
      qc.invalidateQueries({ queryKey: ["portfolio"] });
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setAddingImg(false); }
  };

  const removeWorkImage = async (id) => {
    await api.delete(`/admin/portfolio/${id}`);
    toast.success("Image removed");
    qc.invalidateQueries({ queryKey: ["admin-portfolio"] });
    qc.invalidateQueries({ queryKey: ["portfolio"] });
  };

  const toggleStyle = (slug) => setEditing((ed) => ({ ...ed, styles: ed.styles.includes(slug) ? ed.styles.filter((s) => s !== slug) : [...ed.styles, slug] }));

  const addCustomStyle = () => {
    const slug = customStyle.trim().toLowerCase().replace(/\s+/g, "-");
    if (!slug) return;
    setEditing((ed) => ({ ...ed, styles: ed.styles.includes(slug) ? ed.styles : [...ed.styles, slug] }));
    setCustomStyle("");
  };

  return (
    <AdminLayout title="Artists">
      <button data-testid="add-artist" onClick={() => setEditing(EMPTY)} className="inline-flex items-center gap-2 bg-ember text-obsidian font-mono text-xs uppercase tracking-widest font-semibold px-6 py-3 mb-6 hover:bg-white transition-colors"><Plus size={15} /> Add Artist</button>

      {isLoading ? <p className="text-[#828290] font-mono text-sm">Loading…</p> : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {artists.map((a) => (
            <div key={a.slug} data-testid={`admin-artist-${a.slug}`} className="border border-ash bg-charcoal">
              <div className="aspect-[4/3] overflow-hidden border-b border-ash"><img src={fileUrl(a.photo)} alt={a.name} className="w-full h-full object-cover" /></div>
              <div className="p-4">
                <div className="flex items-center justify-between">
                  <h3 className="font-serif text-2xl text-white">{a.name}</h3>
                  <span className={`font-mono text-[0.55rem] uppercase tracking-widest px-2 py-1 border ${a.active ? "text-green-400 border-green-400/50" : "text-[#828290] border-ash"}`}>{a.active ? "Active" : "Hidden"}</span>
                </div>
                <p className="text-[#828290] text-xs mt-1">{a.role} · {a.experience}</p>
                <div className="flex gap-2 mt-4">
                  <button onClick={() => setEditing({ ...EMPTY, ...a })} data-testid={`edit-artist-${a.slug}`} className="flex-1 border border-ash text-white font-mono text-[0.6rem] uppercase tracking-widest py-2 hover:border-white">Edit</button>
                  <button onClick={() => toggleActive(a)} className="flex-1 border border-ash text-[#C4C4CC] font-mono text-[0.6rem] uppercase tracking-widest py-2 hover:border-white">{a.active ? "Hide" : "Show"}</button>
                  <button onClick={() => remove(a.slug)} className="border border-ash text-red-400 px-3 py-2 hover:border-red-400"><X size={13} /></button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {editing && (
        <div className="fixed inset-0 z-[80] flex justify-end" data-testid="artist-editor">
          <div className="absolute inset-0 bg-obsidian/80 backdrop-blur-sm" onClick={() => setEditing(null)} />
          <div className="relative w-full max-w-lg bg-charcoal border-l border-ash h-full overflow-y-auto p-8 pb-28">
            <div className="flex items-center justify-between mb-8">
              <h2 className="font-serif text-3xl text-white">{editing.slug ? "Edit" : "New"} Artist</h2>
              <button onClick={() => setEditing(null)} className="text-[#828290] hover:text-white"><X size={22} /></button>
            </div>

            <div className="space-y-5">
              <div className="flex items-center gap-4">
                <div className="w-24 h-24 border border-ash overflow-hidden bg-obsidian shrink-0">{editing.photo ? <img src={fileUrl(editing.photo)} alt="" className="w-full h-full object-cover" /> : null}</div>
                <label className="flex items-center gap-2 border border-ash px-4 py-3 cursor-pointer text-[#C4C4CC] font-mono text-xs uppercase tracking-widest hover:border-ember">
                  <Upload size={14} /> {uploading ? "…" : "Profile photo"}
                  <input type="file" accept="image/*" className="hidden" onChange={uploadPhoto} data-testid="artist-photo-upload" />
                </label>
              </div>
              <Inp label="Name" v={editing.name} on={(v) => setEditing({ ...editing, name: v })} tid="artist-name" />
              <Inp label="Slug (url)" v={editing.slug} on={(v) => setEditing({ ...editing, slug: v.toLowerCase().replace(/\s+/g, "-") })} tid="artist-slug" />
              <Inp label="Role" v={editing.role} on={(v) => setEditing({ ...editing, role: v })} />
              <Inp label="Experience" v={editing.experience} on={(v) => setEditing({ ...editing, experience: v })} />
              <Inp label="Instagram (handle)" v={editing.instagram} on={(v) => setEditing({ ...editing, instagram: v })} />
              <Inp label="Email" v={editing.email} on={(v) => setEditing({ ...editing, email: v })} />
              <Inp label="Tagline" v={editing.tagline} on={(v) => setEditing({ ...editing, tagline: v })} />
              <label className="block"><span className="eyebrow block mb-3">Biography</span>
                <textarea rows={6} value={editing.bio} onChange={(e) => setEditing({ ...editing, bio: e.target.value })} className="w-full bg-obsidian border border-ash text-white px-4 py-3 outline-none focus:border-ember resize-none" data-testid="artist-bio" /></label>

              <div>
                <span className="eyebrow block mb-3">Tattoo styles they do</span>
                <div className="flex flex-wrap gap-2">
                  {styles.map((s) => (
                    <button key={s.slug} data-testid={`artist-style-${s.slug}`} onClick={() => toggleStyle(s.slug)} className={`font-mono text-[0.6rem] uppercase tracking-widest px-3 py-1.5 border ${editing.styles.includes(s.slug) ? "bg-ember text-obsidian border-ember" : "border-ash text-[#C4C4CC]"}`}>{s.name}</button>
                  ))}
                  {editing.styles.filter((s) => !styles.some((x) => x.slug === s)).map((s) => (
                    <button key={s} data-testid={`artist-customstyle-${s}`} onClick={() => toggleStyle(s)} className="font-mono text-[0.6rem] uppercase tracking-widest px-3 py-1.5 border bg-ember text-obsidian border-ember flex items-center gap-1">{s.replace(/-/g, " ")} <X size={11} /></button>
                  ))}
                </div>
                <div className="flex gap-2 mt-3">
                  <input value={customStyle} onChange={(e) => setCustomStyle(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addCustomStyle(); } }} placeholder="Type a style and press Add…" data-testid="artist-custom-style" className="flex-1 bg-obsidian border border-ash text-white px-3 py-2 text-sm outline-none focus:border-ember" />
                  <button type="button" data-testid="artist-add-style" onClick={addCustomStyle} className="border border-ash text-white px-4 font-mono text-[0.6rem] uppercase tracking-widest hover:border-ember">Add</button>
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between mb-3">
                  <span className="eyebrow">Work images</span>
                  <label className={`flex items-center gap-2 border border-ash px-3 py-2 cursor-pointer text-[#C4C4CC] font-mono text-[0.6rem] uppercase tracking-widest ${editing.slug ? "hover:border-ember" : "opacity-50 cursor-not-allowed"}`}>
                    <Plus size={13} /> {addingImg ? "…" : "Add image"}
                    <input type="file" accept="image/*" disabled={!editing.slug} className="hidden" onChange={addWorkImage} data-testid="artist-add-image" />
                  </label>
                </div>
                {!editing.slug && <p className="text-[#828290] text-xs mb-3">Publish the artist first, then add their work images.</p>}
                {artistImages.length === 0 ? (
                  <p className="text-[#828290] text-xs">No images yet.</p>
                ) : (
                  <div className="grid grid-cols-3 gap-2" data-testid="artist-work-images">
                    {artistImages.map((p) => (
                      <div key={p.id} className="relative group aspect-square border border-ash overflow-hidden">
                        <img src={fileUrl(p.image)} alt="" className="w-full h-full object-cover" />
                        <button onClick={() => removeWorkImage(p.id)} data-testid={`artist-remove-image-${p.id}`} className="absolute inset-0 flex items-center justify-center bg-obsidian/70 opacity-0 group-hover:opacity-100 transition-opacity text-red-400"><Trash2 size={18} /></button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="absolute bottom-0 right-0 w-full max-w-lg bg-charcoal border-t border-l border-ash p-4">
            <button onClick={save} data-testid="artist-save" className="w-full bg-ember text-obsidian font-mono text-xs uppercase tracking-[0.2em] font-semibold py-4 hover:bg-white transition-colors">Publish</button>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}

function Inp({ label, v, on, tid }) {
  return (
    <label className="block">
      <span className="eyebrow block mb-3">{label}</span>
      <input data-testid={tid} value={v} onChange={(e) => on(e.target.value)} className="w-full bg-obsidian border border-ash text-white px-4 py-3 outline-none focus:border-ember" />
    </label>
  );
}
